import React, { createContext, useContext, useEffect, useState } from 'react';
import supabase from '../supabaseClient';
import { useAuth } from './authContext';

import { AVAILABLE_ROLES } from '../utils/roleHelpers';

const PermissionsContext = createContext({
  roles: [], // Array of role objects { id, role, label, description }
  permissions: [],
  hasPermission: () => false,
  loading: true,
  activeRole: null,
  setActiveRole: () => {},
  isSuperadmin: false,
  ALL_ROLES: AVAILABLE_ROLES,
});

export const PermissionsProvider = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const [roles, setRoles] = useState([]); // Array of role objects
  const [permissions, setPermissions] = useState([]); // Array of permission strings
  const [loading, setLoading] = useState(true);
  const [isAcademyUser, setIsAcademyUser] = useState(false);
  const [activeRole, setActiveRoleState] = useState(null);

  // State for superadmin status
  const [isSuperadmin, setIsSuperadmin] = useState(false);
  
  // Update superadmin status whenever roles change
  useEffect(() => {
    if (roles && roles.length > 0) {
      const hasSuperadminRole = roles.some(r => r.role === 'superadmin');
      console.log('Is superadmin role?', hasSuperadminRole);
      setIsSuperadmin(hasSuperadminRole);
    } else {
      console.log('No roles found, not superadmin');
      setIsSuperadmin(false);
    }
  }, [roles]);

  // Set activeRole and persist
  const setActiveRole = (roleKeyOrId) => {
    console.log('[PermissionsContext] setActiveRole called:', roleKeyOrId);
    setActiveRoleState(roleKeyOrId);
    localStorage.setItem('activeRole', roleKeyOrId);
    console.log('[PermissionsContext] activeRole after set:', roleKeyOrId);
  };

  // On roles/user change, initialize activeRole
  useEffect(() => {
    console.log('[PermissionsContext] roles/user changed:', { roles, user });
    if (!user) {
      setActiveRoleState(null);
      localStorage.removeItem('activeRole');
      console.log('[PermissionsContext] No user, cleared activeRole');
      return;
    }
    // Try to restore from localStorage
    const stored = localStorage.getItem('activeRole');
    if (stored && roles.some(r => r.role === stored || r.id === stored)) {
      setActiveRoleState(stored);
      console.log('[PermissionsContext] Restored activeRole from storage:', stored);
    } else if (roles.length > 0) {
      // Default to highest role
      const highest = roles.reduce((a, b) => {
        const idxA = AVAILABLE_ROLES.findIndex(r => r.key === a.role);
        const idxB = AVAILABLE_ROLES.findIndex(r => r.key === b.role);
        return idxA > idxB ? a : b;
      });
      setActiveRoleState(highest.role);
      localStorage.setItem('activeRole', highest.role);
      console.log('[PermissionsContext] Set activeRole to highest:', highest.role);
    } else {
      setActiveRoleState(null);
      localStorage.removeItem('activeRole');
      console.log('[PermissionsContext] No roles, cleared activeRole');
    }
  }, [roles, user]);

  useEffect(() => {
    if (authLoading || !user) {
      console.log('[PermissionsContext] Auth loading or no user, skipping permissions fetch');
      return;
    }
    
    setLoading(true);
    
    // Superadmin status is now handled by the useEffect hook above
    
    // Fetch all roles for the user (academy_user_roles)
    console.log('[PermissionsContext] Fetching roles for user:', user.id);
    
    // First try to get our roles directly from the database with a simpler query
    // to avoid potential RLS policy recursion issues
    const fetchRolesDirectly = async () => {
      try {
        // Try direct fetch API with minimal query to avoid RLS recursion issues
        const { data: authData } = await supabase.auth.getSession();
        const token = authData?.session?.access_token;
        
        if (!token) {
          console.error('No auth token available for direct fetch');
          throw new Error('Authentication required');
        }
        
        // Use a direct fetch with a simple query that avoids complex joins
        const response = await fetch(
          `${supabase.supabaseUrl}/rest/v1/academy_user_roles?user_id=eq.${user.id}&select=role_id`,
          {
            headers: {
              'apikey': supabase.supabaseKey,
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            }
          }
        );
        
        if (!response.ok) {
          console.error(`Direct fetch failed: ${response.status} ${response.statusText}`);
          throw new Error('Failed to fetch user roles');
        }
        
        const userRoleIds = await response.json();
        console.log('User role IDs fetched via direct API:', userRoleIds);
        
        if (!userRoleIds || userRoleIds.length === 0) {
          console.warn('No role IDs found for user:', user.id);
          setRoles([]);
          setPermissions([]);
          setIsAcademyUser(false);
          setLoading(false);
          return;
        }
        
        // Now fetch the role details for these IDs
        const roleIds = userRoleIds.map(ur => ur.role_id);
        
        // Fetch role details in a separate query
        const rolesResponse = await fetch(
          `${supabase.supabaseUrl}/rest/v1/academy_roles?id=in.(${roleIds.join(',')})&select=id,role,label,description`,
          {
            headers: {
              'apikey': supabase.supabaseKey,
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            }
          }
        );
        
        if (!rolesResponse.ok) {
          console.error(`Role details fetch failed: ${rolesResponse.status} ${rolesResponse.statusText}`);
          throw new Error('Failed to fetch role details');
        }
        
        const roleDetails = await rolesResponse.json();
        console.log('Role details fetched via direct API:', roleDetails);
        
        // Set the roles with the fetched details
        setRoles(roleDetails || []);
        setIsAcademyUser(true);
        
        // Log if we found the superadmin role
        const hasSuperadmin = roleDetails.some(r => r.role === 'superadmin');
        console.log('Has superadmin role (direct fetch):', hasSuperadmin);
        console.log('All roles found:', roleDetails.map(r => `${r.role} (${r.id})`).join(', '));
        
        // Get all permissions via joins after setting roles
        const { data: permsData, error: permsError } = await supabase.rpc('get_user_permissions', { user_id: user.id });
        let perms = [];
        if (Array.isArray(permsData)) {
          perms = permsData.map(p => p.permission);
        }
        setPermissions(perms);
        setLoading(false);
        
      } catch (directFetchError) {
        console.error('Error with direct fetch approach:', directFetchError);
        
        // Fall back to the original method if direct fetch fails
        console.log('Falling back to original Supabase query method');
        
        supabase
          .from('academy_user_roles')
          .select('role_id, role:academy_roles(id, role, label, description)')
          .eq('user_id', user.id)
          .then(async ({ data: userRoles, error }) => {
            console.log('User roles query result (fallback):', { userRoles, error });
            
            if (error) {
              console.error('Error fetching user roles:', error);
              setRoles([]);
              setPermissions([]);
              setIsAcademyUser(false);
              setLoading(false);
              return;
            }
            
            if (!userRoles || userRoles.length === 0) {
              console.warn('No roles found for user:', user.id);
              setRoles([]);
              setPermissions([]);
              setIsAcademyUser(false);
              setLoading(false);
              return;
            }
            
            // Extract role objects and log them
            const roleObjs = userRoles.map(ur => {
              console.log('Processing user role:', ur);
              return ur.role;
            }).filter(Boolean);
            
            console.log('Final role objects:', roleObjs);
            setRoles(roleObjs);
            setIsAcademyUser(true);
            
            // Log if we found the superadmin role
            const hasSuperadmin = roleObjs.some(r => r.role === 'superadmin');
            console.log('Has superadmin role (fallback):', hasSuperadmin);
            console.log('All roles found (fallback):', roleObjs.map(r => `${r.role} (${r.id})`).join(', '));
            
            // Get all permissions via joins
            const { data: permsData, error: permsError } = await supabase.rpc('get_user_permissions', { user_id: user.id });
            let perms = [];
            if (Array.isArray(permsData)) {
              perms = permsData.map(p => p.permission);
            }
            setPermissions(perms);
            setLoading(false);
          });
      }
    };
    
    // Execute the async function
    fetchRolesDirectly();
  }, [user, authLoading]);

  const hasPermission = (perm) => {
    // If user has 'all' permission, grant everything
    if (permissions.includes('all')) return true;
    return permissions.includes(perm);
  };


  return (
    <PermissionsContext.Provider value={{
      roles,
      permissions,
      hasPermission,
      loading,
      isAcademyUser,
      activeRole,
      setActiveRole,
      isSuperadmin,
      ALL_ROLES: AVAILABLE_ROLES
    }}>
      {children}
    </PermissionsContext.Provider>
  );
};

export const usePermissions = () => useContext(PermissionsContext);
